import { Box, Card, CardMedia, Divider, Typography } from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import RefreshIcon from '@mui/icons-material/Refresh'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Alert, Button } from 'ui/baseComponents'
import Loading from 'ui/components/Loading'
import { useTagList } from '../../graphql/tagQueries'
import { highlightsApi } from '../../api/highlightsApi'
import HighlightCardForm from './HighlightCardForm'

const HighlightCardsTab = () => {
  const navigate = useNavigate()
  const [cards, setCards] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [showForm, setShowForm] = useState(false)

  const { data: tagsData } = useTagList()
  const tags = useMemo(() => tagsData?.tags || [], [tagsData])
  const tagMap = Object.fromEntries(tags.map((t) => [t.id, t.tag]))
  const today = new Date().toISOString().split('T')[0]
  const inactiveTagIds = useMemo(
    () =>
      new Set(
        tags
          .filter((t) => !t.active || (t.endDate && t.endDate < today))
          .map((t) => t.id),
      ),
    [tags, today],
  )

  const loadCards = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const result = await highlightsApi.listCards()
      const cardsData = result?.data?.cards || []
      setCards(cardsData)
    } catch (err) {
      setError('Failed to load cards. Please try again.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadCards()
  }, [loadCards])

  const handleCreate = useCallback(
    async (formData) => {
      const result = await highlightsApi.createCard(formData)
      if (result?.error) {
        throw typeof result.error === 'string'
          ? result.error
          : result.error.message || 'Unknown error'
      }
      setShowForm(false)
      const newId = result?.data?.card?.id
      if (newId) {
        navigate(`/admin/outreach/highlight-cards/${newId}`)
      } else {
        loadCards()
      }
    },
    [loadCards, navigate],
  )

  if (loading && cards.length === 0) return <Loading />

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h6">Highlight Cards ({cards.length})</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            type="secondary"
            size="small"
            onClick={loadCards}
            startIcon={<RefreshIcon />}
          >
            Refresh
          </Button>
          <Button
            type="primary"
            size="small"
            onClick={() => setShowForm(true)}
            startIcon={<AddIcon />}
          >
            New Card
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
          gap: 3,
        }}
      >
        {[...cards]
          .sort(
            (a, b) =>
              inactiveTagIds.has(a.tag_id) - inactiveTagIds.has(b.tag_id),
          )
          .map((card) => {
            const isInactive = inactiveTagIds.has(card.tag_id)
            return (
              <Card
                key={card.id}
                elevation={1}
                onClick={() =>
                  navigate(`/admin/outreach/highlight-cards/${card.id}`)
                }
                sx={{
                  borderRadius: '16px !important',
                  overflow: 'hidden',
                  maxWidth: 340,
                  cursor: 'pointer',
                  transition: 'box-shadow 0.2s',
                  display: 'flex',
                  flexDirection: 'column',
                  '&:hover': { boxShadow: 4 },
                  ...(isInactive && {
                    opacity: 0.5,
                    filter: 'grayscale(100%)',
                  }),
                }}
              >
                {card.media_url ? (
                  <CardMedia
                    component="img"
                    image={card.media_url}
                    alt={card.title}
                    sx={{ height: 100, objectFit: 'cover' }}
                  />
                ) : (
                  <Box
                    sx={{
                      height: 100,
                      bgcolor: 'grey.200',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      No image
                    </Typography>
                  </Box>
                )}
                <Box sx={{ px: 2, pt: 1.5, pb: 0.5 }}>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 'bold',
                      color: 'primary.main',
                      textTransform: 'uppercase',
                      fontSize: '0.85rem',
                    }}
                  >
                    {card.title}
                  </Typography>
                </Box>
                <Divider />
                <Box sx={{ px: 2, pt: 1, pb: 2, flex: 1 }}>
                  <Typography
                    variant="body2"
                    sx={{ color: 'text.secondary', lineHeight: 1.5 }}
                  >
                    {card.subtitle || 'No subtitle'}
                  </Typography>
                </Box>
                <Box sx={{ px: 2, pb: 1.5, mt: 'auto' }}>
                  <Typography variant="caption" color="text.disabled">
                    {tagMap[card.tag_id] || card.tag_id}
                  </Typography>
                </Box>
              </Card>
            )
          })}
      </Box>

      <HighlightCardForm
        open={showForm}
        onClose={() => setShowForm(false)}
        onSubmit={handleCreate}
        initialValues={null}
        tags={tags}
      />
    </Box>
  )
}

export default HighlightCardsTab
